/**
 * PointConsumptionStation — App.js 内に常駐する共通ポイント消費通知
 *
 * Features:
 *   - Toast queue (= 同時消費でも順次表示)
 *   - Detail modal (= bundle 機能の sub-task 内訳)
 *   - localStorage 履歴 (= 直近 50 件)
 *   - bottom-right に固定 (= 既存 Snackbar = bottom-left と干渉なし)
 *   - z-index: snackbar - 1 (= Modal/Dialog 開いてる時は隠れる)
 *   - background tab では表示しない (= visibilitychange listen)
 *   - aria-live="polite" (= 視覚障害対応)
 *
 * イベント protocol:
 *   window.dispatchEvent(new CustomEvent('point-consumed', { detail: {
 *     feature: 'ai.writing.content',
 *     featureLabel: '本文生成',
 *     type: 'charged' | 'failure',
 *     usageInfo: { mptCharged, pointsSpent, newBalanceMpt, breakdown, ... },
 *   }}));
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, IconButton, Modal, Fade, Button, Divider,
  Stack, Chip, alpha, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';

const HISTORY_KEY = 'penlytics:point-consumption-history';
const HISTORY_MAX = 50;
const TOAST_DURATION_MS = 3500;

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, HISTORY_MAX) : [];
  } catch { return []; }
};

const saveHistory = (history) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, HISTORY_MAX)));
  } catch { /* quota exceeded - silent ignore */ }
};

export default function PointConsumptionStation() {
  const theme = useTheme();
  const [queue, setQueue] = useState([]);  // { id, ...detail, timestamp }
  const [current, setCurrent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDetail, setModalDetail] = useState(null);
  const timerRef = useRef(null);

  // queue から 1 件取り出して表示
  useEffect(() => {
    if (current || queue.length === 0) return;
    if (document.visibilityState !== 'visible') return; // bg tab では表示しない (= queue に残す)
    const [next, ...rest] = queue;
    setCurrent(next);
    setQueue(rest);
    // TOAST_DURATION_MS 後に消す
    timerRef.current = setTimeout(() => {
      setCurrent(null);
    }, TOAST_DURATION_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [queue, current]);

  // event listener
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail) return;
      const item = {
        id: detail.usageInfo?.idempotencyKey || `${Date.now()}-${Math.random()}`,
        timestamp: Date.now(),
        ...detail,
      };
      // history に追加
      const history = loadHistory();
      history.unshift({
        feature: item.feature,
        featureLabel: item.featureLabel,
        type: item.type,
        pointsSpent: item.usageInfo?.pointsSpent || 0,
        mptCharged: item.usageInfo?.mptCharged || 0,
        breakdown: item.usageInfo?.breakdown || [],
        timestamp: item.timestamp,
      });
      saveHistory(history);
      // queue に push (= 表示待ち)
      setQueue((prev) => [...prev, item]);
    };
    window.addEventListener('point-consumed', handler);
    return () => window.removeEventListener('point-consumed', handler);
  }, []);

  const handleClose = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setCurrent(null);
  }, []);

  const handleClickDetail = useCallback(() => {
    if (!current) return;
    setModalDetail(current);
    setModalOpen(true);
    handleClose();
  }, [current, handleClose]);

  const isFailure = current?.type === 'failure';
  const pointsSpent = current?.usageInfo?.pointsSpent || 0;
  const newBalance = current?.usageInfo?.newBalance;
  const breakdown = current?.usageInfo?.breakdown || [];

  return (
    <>
      {/* Toast */}
      {current && (
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'fixed', bottom: 16, right: 16,
            zIndex: theme.zIndex.snackbar - 1,  // Dialog/Modal より下、 Snackbar より下
            width: 320, maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Fade in={!!current}>
            <Paper
              elevation={6}
              onClick={breakdown.length > 0 ? handleClickDetail : undefined}
              sx={{
                p: 1.5, borderRadius: 2,
                cursor: breakdown.length > 0 ? 'pointer' : 'default',
                bgcolor: isFailure ? alpha(theme.palette.warning.main, 0.08) : alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${isFailure ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.success.main, 0.3)}`,
                display: 'flex', alignItems: 'center', gap: 1.5,
                '&:hover': breakdown.length > 0 ? { boxShadow: 4 } : {},
              }}
            >
              {isFailure
                ? <ErrorOutlineIcon sx={{ color: 'warning.main', fontSize: 22 }} />
                : <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }}>
                  {isFailure
                    ? `${current?.featureLabel || '機能'} 失敗 · ポイント未消費`
                    : `${current?.featureLabel || '機能'} · ${pointsSpent.toFixed(1)} pt 消費`}
                </Typography>
                {!isFailure && newBalance !== undefined && (
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.2 }}>
                    残高 {newBalance.toFixed(1)} pt {breakdown.length > 0 && '· クリックで内訳'}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                sx={{ p: 0.5 }}
                aria-label="閉じる"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Paper>
          </Fade>
        </Box>
      )}

      {/* Detail Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Fade in={modalOpen}>
          <Paper sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 360, maxWidth: '90vw', p: 3, borderRadius: 2, outline: 'none',
          }}>
            {modalDetail && (
              <>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <InfoIcon sx={{ color: 'primary.main' }} />
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                    {modalDetail.featureLabel || modalDetail.feature} · 消費内訳
                  </Typography>
                </Stack>

                {(modalDetail.usageInfo?.breakdown || []).map((sub, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.85rem' }}>
                    <Typography sx={{ fontSize: '0.85rem' }}>
                      {sub.label || sub.subTask}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                      {((sub.mpt || 0) / 1000).toFixed(2)} pt
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>合計</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, fontFamily: 'monospace' }}>
                    {(modalDetail.usageInfo?.pointsSpent || 0).toFixed(2)} pt
                  </Typography>
                </Box>

                {modalDetail.usageInfo?.newBalance !== undefined && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>残高</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                      {modalDetail.usageInfo.newBalance.toFixed(2)} pt
                    </Typography>
                  </Box>
                )}

                <Box sx={{ bgcolor: alpha(theme.palette.info.main, 0.05), p: 1.5, borderRadius: 1, mb: 2 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                    ⓘ 「成功時のみ消費」 が有効です。 AI 失敗時はポイントは消費されません。
                    動的請求モードでは実際の AI コストに応じて消費されます。
                  </Typography>
                </Box>

                {modalDetail.usageInfo?.source && (
                  <Chip
                    label={`source: ${modalDetail.usageInfo.source}${modalDetail.usageInfo.reason ? ` (${modalDetail.usageInfo.reason})` : ''}`}
                    size="small" sx={{ mb: 2, fontSize: '0.7rem' }}
                  />
                )}

                <Button fullWidth variant="outlined" onClick={() => setModalOpen(false)}>閉じる</Button>
              </>
            )}
          </Paper>
        </Fade>
      </Modal>
    </>
  );
}

// 履歴取得用 helper (= DashboardPage 等で参照)
PointConsumptionStation.loadHistory = loadHistory;
PointConsumptionStation.clearHistory = () => {
  try { localStorage.removeItem(HISTORY_KEY); } catch (_) {}
};
